import { useEffect, useMemo, useRef, useState } from "react";
import { LoadingDiv, SweetAlertShow } from "../../master/Master";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import Master from "../Master";
import {
    metaMessageTemplateCreate,
    metaMessageTemplateUploadMedia,
} from "../../../api/Messages";
import { useNavigate } from "react-router-dom";
/* =========================================================
   CONSTANTS
========================================================= */

const LANGUAGES = [
    { value: "en_US", label: "English (US)" },
    { value: "en_GB", label: "English (UK)" },
    { value: "hi", label: "Hindi" },
    { value: "gu", label: "Gujarati" },
    { value: "mr", label: "Marathi" },
    { value: "pa", label: "Punjabi" },
    { value: "bn", label: "Bengali" },
    { value: "ta", label: "Tamil" },
    { value: "te", label: "Telugu" },
    { value: "kn", label: "Kannada" },
    { value: "ml", label: "Malayalam" },
    { value: "ar", label: "Arabic" },
    { value: "es", label: "Spanish" },
    { value: "fr", label: "French" },
    { value: "de", label: "German" },
    { value: "pt_BR", label: "Portuguese (Brazil)" },
];

const HEADER_TYPES = [
    { value: "NONE", label: "None" },
    { value: "TEXT", label: "Text" },
    { value: "IMAGE", label: "Image" },
    { value: "VIDEO", label: "Video" },
    { value: "DOCUMENT", label: "Document" },
    { value: "LOCATION", label: "Location" },
];

const BUTTON_TYPES = [
    { value: "QUICK_REPLY", label: "Quick Reply" },
    { value: "URL", label: "Visit Website" },
    { value: "PHONE_NUMBER", label: "Call Phone Number" },
];

/* =========================================================
   HELPERS
========================================================= */

const getVariables = (text = "") => {
    const matches =
        text.match(/{{\s*(\d+)\s*}}/g) || [];

    return [...new Set(matches)]
        .sort((a, b) => {
            const na = Number(
                a.replace(/[{}\s]/g, "")
            );

            const nb = Number(
                b.replace(/[{}\s]/g, "")
            );

            return na - nb;
        })
        .map((item) =>
            item.replace(/[{}\s]/g, "")
        );
};


const renderWhatsAppText = (
    text = "",
    samples = {}
) => {
    if (!text) return null;

    const tokenPattern =
        /(\*[^\*\n]+\*|_[^_\n]+_|~[^~\n]+~|{{\s*\d+\s*}})/g;

    const parts = text
        .split(tokenPattern)
        .filter((part) => part !== "");

    return parts.map((part, i) => {
        if (/^{{\s*\d+\s*}}$/.test(part)) {
            const num = part.replace(
                /[{}\s]/g,
                ""
            );

            const sample = samples[num];

            return (
                <span
                    key={i}
                    style={{
                        fontWeight: sample
                            ? "inherit"
                            : 600,
                        color: sample
                            ? "inherit"
                            : "#0B6B39",
                        background: sample
                            ? "transparent"
                            : "rgba(18,140,74,0.12)",
                        borderRadius: 4,
                        padding: sample
                            ? 0
                            : "0 4px",
                    }}
                >
                    {sample || part}
                </span>
            );
        }

        if (/^\*[^\*\n]+\*$/.test(part)) {
            return (
                <strong key={i}>
                    {renderWhatsAppText(
                        part.slice(1, -1),
                        samples
                    )}
                </strong>
            );
        }

        if (/^_[^_\n]+_$/.test(part)) {
            return (
                <em key={i}>
                    {renderWhatsAppText(
                        part.slice(1, -1),
                        samples
                    )}
                </em>
            );
        }

        if (/^~[^~\n]+~$/.test(part)) {
            return (
                <s key={i}>
                    {renderWhatsAppText(
                        part.slice(1, -1),
                        samples
                    )}
                </s>
            );
        }

        return (
            <span key={i}>
                {part}
            </span>
        );
    });
};

/* =========================================================
   STYLES
========================================================= */

const PageStyles = () => (
    <style>{`
        .wa-shell {
            --wa-ink: #1F2A24;
            --wa-accent: #128C4A;
            --wa-accent-deep: #0B6B39;
            --wa-line: #E4E9E6;
            --wa-soft: #F5F8F6;
        }

        .wa-eyebrow {
            font-size: 0.75rem;
            letter-spacing: 0.02em;
            color: #6B7C74;
            margin-bottom: 2px;
        }

        .wa-section {
            background: #fff;
            border: 1px solid var(--wa-line);
            border-radius: 14px;
            overflow: hidden;
        }

        .wa-section + .wa-section {
            margin-top: 1.25rem;
        }

        .wa-section-head {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 1rem 1.25rem;
            border-bottom: 1px solid var(--wa-line);
        }

        .wa-section-body {
            padding: 1.25rem;
        }

        .wa-field-label {
            font-size: 0.8rem;
            color: #56645D;
            margin-bottom: 6px;
            font-weight: 500;
        }

        .wa-select,
        .wa-input,
        .wa-textarea {
            width: 100%;
            border: 1px solid var(--wa-line);
            border-radius: 10px;
            padding: 0.6rem 0.8rem;
            font-size: 0.9rem;
            color: var(--wa-ink);
            background: #fff;
            transition: border-color 0.15s ease;
        }

        .wa-select:focus,
        .wa-input:focus,
        .wa-textarea:focus {
            outline: none;
            border-color: var(--wa-accent);
            box-shadow: 0 0 0 3px rgba(18,140,74,0.12);
        }

        .wa-textarea {
            resize: vertical;
            font-family: inherit;
        }

        .wa-hint {
            font-size: 0.78rem;
            color: #8B978F;
            margin-top: 6px;
        }

        .wa-counter {
            font-size: 0.78rem;
            color: #8B978F;
        }

        .wa-format-hint {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
            font-size: 0.76rem;
            color: #8B978F;
            margin-top: 6px;
        }

        .wa-format-hint b {
            color: #56645D;
        }

        .wa-btn-primary {
            background: linear-gradient(
                135deg,
                var(--wa-accent) 0%,
                var(--wa-accent-deep) 100%
            );
            color: #fff;
            border: none;
            border-radius: 10px;
            padding: 0.75rem 2rem;
            font-weight: 600;
            font-size: 0.92rem;
        }

        .wa-btn-primary:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        .wa-btn-ghost {
            background: #fff;
            border: 1px solid var(--wa-line);
            color: #56645D;
            border-radius: 10px;
            padding: 0.55rem 1.1rem;
            font-weight: 600;
            font-size: 0.85rem;
        }

        .wa-btn-add {
            background: var(--wa-soft);
            border: 1px dashed #B9CBC1;
            color: var(--wa-accent-deep);
            border-radius: 8px;
            padding: 0.4rem 0.9rem;
            font-weight: 600;
            font-size: 0.82rem;
        }

        .wa-btn-remove {
            background: #FEF1F1;
            border: none;
            color: #C0392B;
            border-radius: 8px;
            padding: 0.3rem 0.7rem;
            font-weight: 600;
            font-size: 0.78rem;
        }

        .wa-btn-block {
            background: #fff;
            border: 1px solid var(--wa-line);
            border-radius: 10px;
            padding: 1rem;
        }

        .wa-btn-block + .wa-btn-block {
            margin-top: 0.75rem;
        }

        .wa-btn-block-head {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 0.75rem;
        }

        .wa-empty {
            border: 1px dashed var(--wa-line);
            border-radius: 10px;
            padding: 1.5rem;
            text-align: center;
            color: #8B978F;
            font-size: 0.85rem;
        }

        .wa-note {
            background: var(--wa-soft);
            border: 1px solid #DCE7E1;
            color: #4C5E55;
            border-radius: 8px;
            padding: 0.6rem 0.9rem;
            font-size: 0.8rem;
            margin-top: 0.75rem;
        }

        .wa-media-uploaded {
            color: var(--wa-accent-deep);
            background: rgba(18,140,74,.08);
            border: 1px solid rgba(18,140,74,.2);
            border-radius: 8px;
            padding: 8px 10px;
            margin-top: 8px;
            font-size: 0.78rem;
        }

        .wa-phone {
            border-radius: 22px;
            background: #0B141A;
            box-shadow: 0 20px 40px -18px rgba(0,0,0,0.35);
        }

        .wa-phone-inner {
            border-radius: 16px;
            overflow: hidden;
            display: flex;
            flex-direction: column;
        }

        .wa-phone-head {
            background: var(--wa-accent-deep);
            color: #fff;
            height: 58px;
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 0 16px;
        }

        .wa-phone-avatar {
            width: 36px;
            height: 36px;
            border-radius: 50%;
            background: rgba(255,255,255,0.18);
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 700;
            font-size: 0.9rem;
        }

        .wa-phone-body {
            padding: 16px 12px;
            min-height: 420px;
            background-color: #E9DFCF;
            background-image:
                radial-gradient(
                    rgba(0,0,0,0.035) 1px,
                    transparent 1px
                ),
                radial-gradient(
                    rgba(0,0,0,0.035) 1px,
                    transparent 1px
                );
            background-size: 26px 26px;
            background-position: 0 0, 13px 13px;
        }

        .wa-bubble {
            position: relative;
            background: #fff;
            border-radius: 10px;
            padding: 9px 10px 7px;
            max-width: 94%;
            box-shadow: 0 1px 1.5px rgba(0,0,0,0.15);
            font-size: 0.86rem;
            line-height: 1.45;
            color: #111B21;
        }

        .wa-bubble-time {
            text-align: right;
            font-size: 0.68rem;
            color: #8696A0;
            margin-top: 4px;
        }

        .wa-bubble-btn {
            text-align: center;
            padding: 9px 0;
            color: #00a5f4;
            font-weight: 500;
            font-size: 0.85rem;
        }

        .wa-bubble-btn + .wa-bubble-btn {
            border-top: 1px solid #F0F2F1;
        }

        .wa-bubble-media {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 4px;
            background: #F0F2F1;
            border-radius: 8px;
            padding: 26px 0;
            margin-bottom: 6px;
            color: #8696A0;
            font-size: 0.78rem;
        }

        .wa-otp-pill {
            display: inline-block;
            background: var(--wa-soft);
            color: var(--wa-accent-deep);
            border-radius: 6px;
            padding: 4px 10px;
            font-weight: 700;
            letter-spacing: 1px;
            margin-top: 6px;
        }

        @media (max-width: 991px) {
            .wa-sticky {
                position: static !important;
            }
        }
    `}</style>
);

/* =========================================================
   ICON
========================================================= */

const Icon = ({
    path,
    size = 18,
    ...rest
}) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...rest}
    >
        <path d={path} />
    </svg>
);

const ICONS = {
    image:
        "M3 3h18v18H3z M8.5 8.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3 M21 15l-5-5L5 21",

    video:
        "m22 8-6 4 6 4V8Z M2 6h14v12H2z",

    file:
        "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6",

    pin:
        "M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 1 1 16 0Z M12 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4",
};

/* =========================================================
   COMPONENT
========================================================= */

export default function MetaWaTemplatesCreate() {
    const navigate = useNavigate();


    const [loading, setLoading] = useState(false);
    const bodyTextareaRef = useRef(null);


    const [form, setForm] = useState({
        name: "",
        category: "UTILITY",
        language: "en_US",
        allow_category_change: true,

        header_type: "NONE",
        header_text: "",
        header_sample: "",
        header_handle: "",

        body_text: "",
        footer_text: "",

        buttons: [],

        auth_add_security_recommendation: true,
        auth_code_expiration_minutes: 10,
        auth_otp_type: "COPY_CODE",
        auth_button_text: "Copy Code",
        auth_autofill_text: "",
        auth_package_name: "",
        auth_signature_hash: "",
    });

    const [bodySamples, setBodySamples] = useState({});
    const [headerFile, setHeaderFile] = useState(null);
    const [headerUploading, setHeaderUploading] =
        useState(false);

    /* =====================================================
       PREVIEW URL
    ===================================================== */

    const headerPreviewUrl = useMemo(() => {
        if (!headerFile) return null;

        return URL.createObjectURL(headerFile);
    }, [headerFile]);

    useEffect(() => {
        return () => {
            if (headerPreviewUrl) {
                URL.revokeObjectURL(
                    headerPreviewUrl
                );
            }
        };
    }, [headerPreviewUrl]);

    /* =====================================================
       VARIABLES
    ===================================================== */

    const bodyVariables = useMemo(
        () => getVariables(form.body_text),
        [form.body_text]
    );

    const headerVariables = useMemo(
        () => getVariables(form.header_text),
        [form.header_text]
    );

    const headerSamplesMap = useMemo(() => {
        if (!headerVariables[0]) {
            return {};
        }

        return {
            [headerVariables[0]]:
                form.header_sample,
        };
    }, [
        headerVariables,
        form.header_sample,
    ]);

    useEffect(() => {
        setBodySamples((old) => {
            const updated = {};

            bodyVariables.forEach(
                (variable) => {
                    updated[variable] =
                        old[variable] || "";
                }
            );

            return updated;
        });
    }, [bodyVariables]);

    /* =====================================================
       FORM UPDATE
    ===================================================== */

    const updateForm = (key, value) => {
        setForm((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const insertBodyVariable = () => {
        const textarea = bodyTextareaRef.current;
        const currentText = form.body_text || "";

        const variables = getVariables(currentText);

        const usedNumbers = variables
            .map(Number)
            .filter((n) => !Number.isNaN(n));

        // Next variable: 1, 2, 3, 4...
        const nextNumber =
            usedNumbers.length > 0
                ? Math.max(...usedNumbers) + 1
                : 1;

        const variable = `{{${nextNumber}}}`;

        const start =
            textarea?.selectionStart ?? currentText.length;

        const end =
            textarea?.selectionEnd ?? currentText.length;

        const newText =
            currentText.slice(0, start) +
            variable +
            currentText.slice(end);

        updateForm("body_text", newText);

        // Cursor ko inserted variable ke baad set karo
        requestAnimationFrame(() => {
            const el = bodyTextareaRef.current;

            if (!el) return;

            const newCursor =
                start + variable.length;

            el.focus();
            el.setSelectionRange(
                newCursor,
                newCursor
            );
        });
    };





    // const updateForm = (
    //     key,
    //     value
    // ) => {
    //     setForm((old) => ({
    //         ...old,
    //         [key]: value,
    //     }));
    // };

    /* =====================================================
       HEADER MEDIA UPLOAD
    ===================================================== */

    const handleHeaderFile = async (
        event
    ) => {
        const file =
            event.target.files?.[0];

        if (!file) {
            setHeaderFile(null);
            updateForm(
                "header_handle",
                ""
            );
            return;
        }

        setHeaderFile(file);

        updateForm(
            "header_handle",
            ""
        );

        setHeaderUploading(true);

        try {
            const uploadData =
                new FormData();

            uploadData.append(
                "file",
                file
            );

            uploadData.append(
                "type",
                form.header_type
            );

            /*
             * IMPORTANT:
             * API helper use ho raha hai.
             *
             * Authorization:
             * api.js automatically bhejega.
             *
             * React se token/user manually
             * bhejne ki zarurat nahi hai.
             */

            const response =
                await metaMessageTemplateUploadMedia(
                    uploadData
                );

            const data =
                response?.data;

            if (
                !data?.success ||
                !data?.handle
            ) {
                const metaError =
                    data?.meta_error?.error ||
                    data?.error?.error;

                const message =
                    metaError?.error_user_msg ||
                    metaError?.message ||
                    data?.message ||
                    "Media upload failed.";

                throw new Error(
                    message
                );
            }

            /*
             * Meta ka returned media handle
             * save karo.
             */

            updateForm(
                "header_handle",
                data.handle
            );

            SweetAlertShow(
                "success",
                "Media Uploaded",
                "Media uploaded to Meta successfully."
            );
        } catch (error) {
            console.error(
                "Meta Media Upload Error:",
                error?.response?.data ||
                error
            );

            const errorData =
                error?.response?.data;

            const metaError =
                errorData?.meta_error?.error ||
                errorData?.error?.error;

            const message =
                metaError?.error_user_msg ||
                metaError?.message ||
                errorData?.message ||
                error?.message ||
                "Unable to upload media to Meta.";

            setHeaderFile(null);

            updateForm(
                "header_handle",
                ""
            );

            SweetAlertShow(
                "error",
                "Upload Failed",
                message
            );
        } finally {
            setHeaderUploading(
                false
            );
        }
    };

    /* =====================================================
       BUTTONS
    ===================================================== */

    const addButton = () => {
        if (
            form.buttons.length >= 3
        ) {
            SweetAlertShow(
                "info",
                "Maximum Buttons",
                "Maximum 3 buttons can be added."
            );

            return;
        }

        setForm((old) => ({
            ...old,
            buttons: [
                ...old.buttons,
                {
                    type: "QUICK_REPLY",
                    text: "",
                    url: "",
                    url_example: "",
                    phone_number: "",
                },
            ],
        }));
    };

    const updateButton = (
        index,
        key,
        value
    ) => {
        setForm((old) => {
            const buttons = [
                ...old.buttons,
            ];

            buttons[index] = {
                ...buttons[index],
                [key]: value,
            };

            return {
                ...old,
                buttons,
            };
        });
    };

    const removeButton = (
        index
    ) => {
        setForm((old) => ({
            ...old,
            buttons: old.buttons.filter(
                (_, i) =>
                    i !== index
            ),
        }));
    };

    /* =====================================================
       SUBMIT TEMPLATE
    ===================================================== */

    const submitTemplate = async (
        event
    ) => {
        event.preventDefault();

        /* ================================================
           BASIC VALIDATION
        ================================================= */

        if (!form.name.trim()) {
            SweetAlertShow(
                "warning",
                "Validation",
                "Template name is required."
            );
            return;
        }

        if (
            !/^[a-z0-9_]+$/.test(
                form.name
            )
        ) {
            SweetAlertShow(
                "warning",
                "Validation",
                "Template name can contain only lowercase letters, numbers and underscore."
            );
            return;
        }

        if (
            form.category !==
            "AUTHENTICATION" &&
            !form.body_text.trim()
        ) {
            SweetAlertShow(
                "warning",
                "Validation",
                "Body text is required."
            );
            return;
        }

        if (
            form.header_type ===
            "TEXT" &&
            !form.header_text.trim()
        ) {
            SweetAlertShow(
                "warning",
                "Validation",
                "Header text is required."
            );
            return;
        }

        /* ================================================
           HEADER TEXT VARIABLE
        ================================================= */

        if (
            form.header_type ===
            "TEXT" &&
            headerVariables.length > 0
        ) {
            if (
                headerVariables.length !==
                1 ||
                headerVariables[0] !==
                "1"
            ) {
                SweetAlertShow(
                    "warning",
                    "Validation",
                    "Header can use only {{1}}."
                );

                return;
            }

            if (
                !form.header_sample.trim()
            ) {
                SweetAlertShow(
                    "warning",
                    "Validation",
                    "Header sample value is required."
                );

                return;
            }
        }

        /* ================================================
           MEDIA UPLOAD CHECK
        ================================================= */

        if (headerUploading) {
            SweetAlertShow(
                "info",
                "Please wait",
                "Header media is still uploading to Meta."
            );

            return;
        }

        if (
            [
                "IMAGE",
                "VIDEO",
                "DOCUMENT",
            ].includes(
                form.header_type
            ) &&
            !form.header_handle
        ) {
            SweetAlertShow(
                "warning",
                "Media Required",
                "Please select and upload header media first."
            );

            return;
        }

        /* ================================================
           BODY VARIABLE CHECK
        ================================================= */

        if (
            form.category !==
            "AUTHENTICATION" &&
            bodyVariables.length > 0
        ) {
            const expected =
                Array.from(
                    {
                        length:
                            bodyVariables.length,
                    },
                    (_, index) =>
                        String(index + 1)
                );

            if (
                JSON.stringify(
                    bodyVariables
                ) !==
                JSON.stringify(
                    expected
                )
            ) {
                SweetAlertShow(
                    "warning",
                    "Validation",
                    "Body variables must be sequential: {{1}}, {{2}}, {{3}}..."
                );

                return;
            }

            for (
                const variable of bodyVariables
            ) {
                if (
                    !bodySamples[
                        variable
                    ]?.trim()
                ) {
                    SweetAlertShow(
                        "warning",
                        "Sample Required",
                        `Please enter sample value for {{${variable}}}.`
                    );

                    return;
                }
            }
        }

        /* ================================================
           BODY SAMPLES
        ================================================= */

        const body_samples = {};

        bodyVariables.forEach(
            (variable) => {
                body_samples[
                    variable
                ] =
                    bodySamples[
                    variable
                    ] || "";
            }
        );

        /* ================================================
           FORM DATA
        ================================================= */

        const formData =
            new FormData();

        formData.append(
            "name",
            form.name.trim()
        );

        formData.append(
            "category",
            form.category
        );

        formData.append(
            "language",
            form.language
        );

        formData.append(
            "allow_category_change",
            form.allow_category_change
                ? "1"
                : "0"
        );

        formData.append(
            "header_type",
            form.header_type
        );

        /* ================================================
           TEXT HEADER
        ================================================= */

        if (
            form.header_type ===
            "TEXT"
        ) {
            formData.append(
                "header_text",
                form.header_text.trim()
            );

            if (
                form.header_sample.trim()
            ) {
                formData.append(
                    "header_sample",
                    form.header_sample.trim()
                );
            }
        }

        /* ================================================
           MEDIA HEADER

           IMPORTANT:
           header_media nahi bhejna.

           Media pehle upload-media API se
           Meta par upload ho chuka hai.

           Sirf Meta returned handle bhejna hai.
        ================================================= */

        if (
            [
                "IMAGE",
                "VIDEO",
                "DOCUMENT",
            ].includes(
                form.header_type
            )
        ) {
            formData.append(
                "header_handle",
                form.header_handle
            );
        }

        /* ================================================
           STANDARD TEMPLATE
        ================================================= */

        if (
            form.category !==
            "AUTHENTICATION"
        ) {
            formData.append(
                "body_text",
                form.body_text.trim()
            );

            formData.append(
                "body_samples",
                JSON.stringify(
                    body_samples
                )
            );

            if (
                form.footer_text.trim()
            ) {
                formData.append(
                    "footer_text",
                    form.footer_text.trim()
                );
            }

            formData.append(
                "buttons",
                JSON.stringify(
                    form.buttons
                )
            );
        }

        /* ================================================
           AUTHENTICATION
        ================================================= */

        formData.append(
            "auth_add_security_recommendation",
            form.auth_add_security_recommendation
                ? "1"
                : "0"
        );

        formData.append(
            "auth_code_expiration_minutes",
            String(
                form.auth_code_expiration_minutes ||
                10
            )
        );

        formData.append(
            "auth_otp_type",
            form.auth_otp_type
        );

        formData.append(
            "auth_button_text",
            form.auth_button_text ||
            ""
        );

        formData.append(
            "auth_autofill_text",
            form.auth_autofill_text ||
            ""
        );

        formData.append(
            "auth_package_name",
            form.auth_package_name ||
            ""
        );

        formData.append(
            "auth_signature_hash",
            form.auth_signature_hash ||
            ""
        );

        /* ================================================
           API CALL
        ================================================= */

        setLoading(true);

        try {
            const response =
                await metaMessageTemplateCreate(
                    formData
                );

            const data =
                response?.data;

            if (
                data?.status ||
                data?.success
            ) {
                SweetAlertShow(
                    "success",
                    "Success",
                    data.message ||
                    "WhatsApp template created successfully."
                );

                setForm({
                    name: "",
                    category: "UTILITY",
                    language: "en_US",
                    allow_category_change: true,

                    header_type: "NONE",
                    header_text: "",
                    header_sample: "",
                    header_handle: "",

                    body_text: "",
                    footer_text: "",

                    buttons: [],

                    auth_add_security_recommendation: true,
                    auth_code_expiration_minutes: 10,
                    auth_otp_type: "COPY_CODE",
                    auth_button_text: "Copy Code",
                    auth_autofill_text: "",
                    auth_package_name: "",
                    auth_signature_hash: "",
                });

                setBodySamples({});
                setHeaderFile(null);

                // Success ke baad previous page par wapas
                setTimeout(() => {
                    navigate(-1);
                }, 1200);
            } else {
                const metaError =
                    data?.error
                        ?.error ||
                    data?.meta_error
                        ?.error;

                const message =
                    metaError?.error_user_msg ||
                    metaError?.message ||
                    data?.message ||
                    "Template creation failed.";

                SweetAlertShow(
                    "error",
                    "Meta API Error",
                    message
                );
            }
        } catch (error) {
            console.error(
                "Meta Template Create Error:",
                error?.response
                    ?.data ||
                error
            );

            const errorData =
                error?.response?.data;

            const metaError =
                errorData?.error
                    ?.error ||
                errorData
                    ?.meta_error
                    ?.error;

            const message =
                metaError?.error_user_msg ||
                metaError?.message ||
                errorData?.message ||
                error?.message ||
                "Something went wrong while creating template.";

            SweetAlertShow(
                "error",
                "Meta API Error",
                message
            );
        } finally {
            setLoading(
                false
            );
        }
    };

    /* =====================================================
       MEDIA META
    ===================================================== */

    const mediaMeta = {
        IMAGE: {
            icon: ICONS.image,
            label: "Image",
        },

        VIDEO: {
            icon: ICONS.video,
            label: "Video",
        },

        DOCUMENT: {
            icon: ICONS.file,
            label: "Document",
        },
    };

    /* =====================================================
       RETURN
    ===================================================== */

    return (
        <>
            <PageStyles />

            {loading && (
                <LoadingDiv />
            )}

            <div className="layout-wrapper layout-content-navbar wa-shell">
                <div className="layout-container">

                    <Header />

                    <div className="layout-page">

                        <Nav />

                        <div className="content-wrapper">

                            <div className="container-fluid flex-grow-1 container-p-y">

                                <div className="row">

                                    <Master />

                                    {/* PAGE HEADER */}

                                    <div className="col-12 mb-4">

                                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">

                                            <div>

                                                <div className="wa-eyebrow">
                                                    Meta WhatsApp
                                                </div>

                                                <h4
                                                    className="mb-1"
                                                    style={{
                                                        color:
                                                            "var(--wa-ink)",
                                                    }}
                                                >
                                                    Create template
                                                </h4>

                                                <p
                                                    className="text-muted mb-0"
                                                    style={{
                                                        fontSize:
                                                            "0.9rem",
                                                    }}
                                                >
                                                    Fill in the content — the phone on the right updates as you type.
                                                </p>

                                            </div>

                                            <button
                                                type="button"
                                                className="wa-btn-ghost"
                                                onClick={() =>
                                                    window.history.back()
                                                }
                                            >
                                                Back
                                            </button>

                                        </div>

                                    </div>

                                    <form
                                        onSubmit={
                                            submitTemplate
                                        }
                                        style={{
                                            width:
                                                "100%",
                                        }}
                                    >

                                        <div className="row g-4">

                                            {/* =================================================
                                                LEFT FORM
                                            ================================================= */}

                                            <div className="col-lg-7">

                                                {/* BASIC INFORMATION */}

                                                <div className="wa-section">

                                                    <div className="wa-section-head">

                                                        <h6 className="mb-0">
                                                            Basic information
                                                        </h6>

                                                    </div>

                                                    <div className="wa-section-body">

                                                        <div className="row">

                                                            <div className="col-md-6 mb-3">

                                                                <div className="wa-field-label">
                                                                    Template name
                                                                </div>

                                                                <input
                                                                    type="text"
                                                                    className="wa-input"
                                                                    value={
                                                                        form.name
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        updateForm(
                                                                            "name",
                                                                            e.target.value
                                                                                .toLowerCase()
                                                                                .replace(
                                                                                    /\s+/g,
                                                                                    "_"
                                                                                )
                                                                                .replace(
                                                                                    /[^a-z0-9_]/g,
                                                                                    ""
                                                                                )
                                                                        )
                                                                    }
                                                                    placeholder="student_fee_reminder"
                                                                />

                                                                <div className="wa-hint">
                                                                    Lowercase letters, numbers and underscore only.
                                                                </div>

                                                            </div>

                                                            <div className="col-md-3 mb-3">

                                                                <div className="wa-field-label">
                                                                    Category
                                                                </div>

                                                                <select
                                                                    className="wa-select"
                                                                    value={
                                                                        form.category
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        updateForm(
                                                                            "category",
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                >
                                                                    <option value="MARKETING">
                                                                        MARKETING
                                                                    </option>

                                                                    <option value="UTILITY">
                                                                        UTILITY
                                                                    </option>

                                                                    <option value="AUTHENTICATION">
                                                                        AUTHENTICATION
                                                                    </option>
                                                                </select>

                                                            </div>

                                                            <div className="col-md-3 mb-3">

                                                                <div className="wa-field-label">
                                                                    Language
                                                                </div>

                                                                <select
                                                                    className="wa-select"
                                                                    value={
                                                                        form.language
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        updateForm(
                                                                            "language",
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                >
                                                                    {LANGUAGES.map(
                                                                        (
                                                                            language
                                                                        ) => (
                                                                            <option
                                                                                key={
                                                                                    language.value
                                                                                }
                                                                                value={
                                                                                    language.value
                                                                                }
                                                                            >
                                                                                {
                                                                                    language.label
                                                                                }
                                                                            </option>
                                                                        )
                                                                    )}
                                                                </select>

                                                            </div>

                                                            <div className="col-12">

                                                                <label
                                                                    className="d-flex align-items-center gap-2"
                                                                    style={{
                                                                        fontSize:
                                                                            "0.88rem",
                                                                        cursor:
                                                                            "pointer",
                                                                    }}
                                                                >

                                                                    <input
                                                                        type="checkbox"
                                                                        checked={
                                                                            form.allow_category_change
                                                                        }
                                                                        onChange={(
                                                                            e
                                                                        ) =>
                                                                            updateForm(
                                                                                "allow_category_change",
                                                                                e.target.checked
                                                                            )
                                                                        }
                                                                        style={{
                                                                            accentColor:
                                                                                "var(--wa-accent)",
                                                                            width: 16,
                                                                            height: 16,
                                                                        }}
                                                                    />

                                                                    Allow Meta to correct the category

                                                                </label>

                                                            </div>

                                                        </div>

                                                    </div>

                                                </div>

                                                {/* =================================================
                                                    STANDARD TEMPLATE
                                                ================================================= */}

                                                {form.category !==
                                                    "AUTHENTICATION" ? (
                                                    <>

                                                        {/* HEADER */}

                                                        <div className="wa-section">

                                                            <div className="wa-section-head">

                                                                <h6 className="mb-0">
                                                                    Header
                                                                </h6>

                                                            </div>

                                                            <div className="wa-section-body">

                                                                <div className="mb-3">

                                                                    <div className="wa-field-label">
                                                                        Header type
                                                                    </div>

                                                                    <select
                                                                        className="wa-select"
                                                                        value={
                                                                            form.header_type
                                                                        }
                                                                        onChange={(
                                                                            e
                                                                        ) => {

                                                                            const type =
                                                                                e.target.value;

                                                                            updateForm(
                                                                                "header_type",
                                                                                type
                                                                            );

                                                                            updateForm(
                                                                                "header_handle",
                                                                                ""
                                                                            );

                                                                            updateForm(
                                                                                "header_text",
                                                                                ""
                                                                            );

                                                                            updateForm(
                                                                                "header_sample",
                                                                                ""
                                                                            );

                                                                            setHeaderFile(
                                                                                null
                                                                            );

                                                                        }}
                                                                    >

                                                                        {HEADER_TYPES.map(
                                                                            (
                                                                                type
                                                                            ) => (
                                                                                <option
                                                                                    key={
                                                                                        type.value
                                                                                    }
                                                                                    value={
                                                                                        type.value
                                                                                    }
                                                                                >
                                                                                    {
                                                                                        type.label
                                                                                    }
                                                                                </option>
                                                                            )
                                                                        )}

                                                                    </select>

                                                                </div>

                                                                {/* TEXT HEADER */}

                                                                {form.header_type ===
                                                                    "TEXT" && (
                                                                        <>

                                                                            <div className="mb-3">

                                                                                <div className="wa-field-label">
                                                                                    Header text
                                                                                </div>

                                                                                <input
                                                                                    type="text"
                                                                                    maxLength={
                                                                                        60
                                                                                    }
                                                                                    className="wa-input"
                                                                                    value={
                                                                                        form.header_text
                                                                                    }
                                                                                    onChange={(
                                                                                        e
                                                                                    ) =>
                                                                                        updateForm(
                                                                                            "header_text",
                                                                                            e.target.value
                                                                                        )
                                                                                    }
                                                                                    placeholder="Hello {{1}}"
                                                                                />

                                                                                <div className="d-flex justify-content-between">

                                                                                    <span className="wa-hint mb-0">
                                                                                        One variable max —{" "}
                                                                                        {"{{1}}"}
                                                                                    </span>

                                                                                    <span className="wa-counter">
                                                                                        {
                                                                                            form
                                                                                                .header_text
                                                                                                .length
                                                                                        }
                                                                                        /60
                                                                                    </span>

                                                                                </div>

                                                                            </div>

                                                                            {headerVariables.length >
                                                                                0 && (
                                                                                    <div className="mb-3">

                                                                                        <div className="wa-field-label">
                                                                                            Sample value for{" "}
                                                                                            {`{{${headerVariables[0]}}}`}
                                                                                        </div>

                                                                                        <input
                                                                                            type="text"
                                                                                            className="wa-input"
                                                                                            value={
                                                                                                form.header_sample
                                                                                            }
                                                                                            onChange={(
                                                                                                e
                                                                                            ) =>
                                                                                                updateForm(
                                                                                                    "header_sample",
                                                                                                    e.target.value
                                                                                                )
                                                                                            }
                                                                                            placeholder="e.g. Rahul"
                                                                                        />

                                                                                    </div>
                                                                                )}

                                                                        </>
                                                                    )}

                                                                {/* MEDIA */}

                                                                {[
                                                                    "IMAGE",
                                                                    "VIDEO",
                                                                    "DOCUMENT",
                                                                ].includes(
                                                                    form.header_type
                                                                ) && (
                                                                        <div>

                                                                            <div className="wa-field-label">
                                                                                Upload header media
                                                                            </div>

                                                                            <label
                                                                                className="d-flex align-items-center gap-2 wa-input"
                                                                                style={{
                                                                                    cursor:
                                                                                        headerUploading
                                                                                            ? "not-allowed"
                                                                                            : "pointer",

                                                                                    opacity:
                                                                                        headerUploading
                                                                                            ? 0.6
                                                                                            : 1,
                                                                                }}
                                                                            >

                                                                                <Icon
                                                                                    path={
                                                                                        mediaMeta[
                                                                                            form.header_type
                                                                                        ].icon
                                                                                    }
                                                                                    size={
                                                                                        16
                                                                                    }
                                                                                />

                                                                                <span
                                                                                    className="text-truncate"
                                                                                    style={{
                                                                                        fontSize:
                                                                                            "0.85rem",

                                                                                        color:
                                                                                            headerFile
                                                                                                ? "var(--wa-ink)"
                                                                                                : "#8B978F",
                                                                                    }}
                                                                                >
                                                                                    {headerUploading
                                                                                        ? "Uploading to Meta..."
                                                                                        : headerFile
                                                                                            ? headerFile.name
                                                                                            : "Choose a file"}
                                                                                </span>

                                                                                <input
                                                                                    type="file"
                                                                                    className="d-none"
                                                                                    disabled={
                                                                                        headerUploading
                                                                                    }
                                                                                    accept={
                                                                                        form.header_type ===
                                                                                            "IMAGE"
                                                                                            ? "image/jpeg,image/png,image/webp"
                                                                                            : form.header_type ===
                                                                                                "VIDEO"
                                                                                                ? "video/mp4,video/3gpp"
                                                                                                : ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                                                                                    }
                                                                                    onChange={
                                                                                        handleHeaderFile
                                                                                    }
                                                                                />

                                                                            </label>

                                                                            {headerUploading && (
                                                                                <div className="wa-note">
                                                                                    ⏳ Uploading media to Meta. Please wait...
                                                                                </div>
                                                                            )}

                                                                            {!headerUploading &&
                                                                                form.header_handle && (
                                                                                    <div className="wa-media-uploaded">
                                                                                        ✓ Media uploaded to Meta successfully.
                                                                                    </div>
                                                                                )}

                                                                            {!headerUploading &&
                                                                                !form.header_handle && (
                                                                                    <div className="wa-note">
                                                                                        Select media. It will be uploaded to Meta automatically.
                                                                                    </div>
                                                                                )}

                                                                        </div>
                                                                    )}

                                                                {/* LOCATION */}

                                                                {form.header_type ===
                                                                    "LOCATION" && (
                                                                        <div className="wa-note mt-0">
                                                                            A location header will be attached when this template is sent.
                                                                        </div>
                                                                    )}

                                                            </div>

                                                        </div>

                                                        {/* BODY */}

                                                        <div className="wa-section">

                                                            <div className="wa-section-head">

                                                                <h6 className="mb-0">
                                                                    Body
                                                                </h6>

                                                            </div>

                                                            <div className="wa-section-body">

                                                                <div className="wa-field-label">
                                                                    Body text
                                                                </div>

                                                                <textarea
                                                                    id="wa-body-textarea"
                                                                    className="wa-textarea"
                                                                    rows={
                                                                        7
                                                                    }
                                                                    maxLength={
                                                                        1024
                                                                    }
                                                                    value={
                                                                        form.body_text
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        updateForm(
                                                                            "body_text",
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    placeholder="Hello {{1}}, your fee of {{2}} is pending."
                                                                />

                                                                <div className="d-flex justify-content-between align-items-center mt-1">

                                                                    <div className="d-flex align-items-center gap-2 flex-wrap">

                                                                        <span className="wa-hint mb-0">
                                                                            Variables:{" "}
                                                                            {"{{1}}"},{" "}
                                                                            {"{{2}}"}...
                                                                        </span>

                                                                        <button
                                                                            type="button"
                                                                            className="wa-btn-add"
                                                                            onClick={insertBodyVariable}
                                                                        >
                                                                            + Add Variable
                                                                        </button>

                                                                    </div>

                                                                    <span className="wa-counter">
                                                                        {
                                                                            form
                                                                                .body_text
                                                                                .length
                                                                        }
                                                                        /1024
                                                                    </span>

                                                                </div>

                                                                <div className="wa-format-hint">

                                                                    <span>
                                                                        <b>
                                                                            *bold*
                                                                        </b>{" "}
                                                                        → bold
                                                                    </span>

                                                                    <span>
                                                                        <b>
                                                                            _italic_
                                                                        </b>{" "}
                                                                        → italic
                                                                    </span>

                                                                    <span>
                                                                        <b>
                                                                            ~strike~
                                                                        </b>{" "}
                                                                        → strikethrough
                                                                    </span>

                                                                </div>

                                                                {bodyVariables.length >
                                                                    0 && (
                                                                        <div className="mt-4">

                                                                            <div className="wa-field-label mb-2">
                                                                                Sample values
                                                                            </div>

                                                                            <div className="row">

                                                                                {bodyVariables.map(
                                                                                    (
                                                                                        variable
                                                                                    ) => (
                                                                                        <div
                                                                                            className="col-md-6 mb-3"
                                                                                            key={
                                                                                                variable
                                                                                            }
                                                                                        >

                                                                                            <div className="wa-field-label">
                                                                                                {`{{${variable}}}`}
                                                                                            </div>

                                                                                            <input
                                                                                                type="text"
                                                                                                className="wa-input"
                                                                                                value={
                                                                                                    bodySamples[
                                                                                                    variable
                                                                                                    ] ||
                                                                                                    ""
                                                                                                }
                                                                                                onChange={(
                                                                                                    e
                                                                                                ) =>
                                                                                                    setBodySamples(
                                                                                                        (
                                                                                                            old
                                                                                                        ) => ({
                                                                                                            ...old,
                                                                                                            [variable]:
                                                                                                                e
                                                                                                                    .target
                                                                                                                    .value,
                                                                                                        })
                                                                                                    )
                                                                                                }
                                                                                                placeholder={`Example value for {{${variable}}}`}
                                                                                            />

                                                                                        </div>
                                                                                    )
                                                                                )}

                                                                            </div>

                                                                        </div>
                                                                    )}

                                                            </div>

                                                        </div>

                                                        {/* FOOTER */}

                                                        <div className="wa-section">

                                                            <div className="wa-section-head">

                                                                <h6 className="mb-0">
                                                                    Footer
                                                                </h6>

                                                            </div>

                                                            <div className="wa-section-body">

                                                                <input
                                                                    type="text"
                                                                    maxLength={
                                                                        60
                                                                    }
                                                                    className="wa-input"
                                                                    value={
                                                                        form.footer_text
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        updateForm(
                                                                            "footer_text",
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    placeholder="Thank you"
                                                                />

                                                                <div className="wa-counter mt-1">
                                                                    {
                                                                        form
                                                                            .footer_text
                                                                            .length
                                                                    }
                                                                    /60
                                                                </div>

                                                            </div>

                                                        </div>

                                                        {/* BUTTONS */}

                                                        <div className="wa-section">

                                                            <div className="wa-section-head justify-content-between">

                                                                <h6 className="mb-0">
                                                                    Buttons
                                                                </h6>

                                                                <button
                                                                    type="button"
                                                                    className="wa-btn-add"
                                                                    onClick={
                                                                        addButton
                                                                    }
                                                                >
                                                                    + Add button
                                                                </button>

                                                            </div>

                                                            <div className="wa-section-body">

                                                                {form.buttons.length ===
                                                                    0 && (
                                                                        <div className="wa-empty">
                                                                            No buttons yet — quick reply, website link, or call.
                                                                        </div>
                                                                    )}

                                                                {form.buttons.map(
                                                                    (
                                                                        button,
                                                                        index
                                                                    ) => (
                                                                        <div
                                                                            className="wa-btn-block"
                                                                            key={
                                                                                index
                                                                            }
                                                                        >

                                                                            <div className="wa-btn-block-head">

                                                                                <strong
                                                                                    style={{
                                                                                        fontSize:
                                                                                            "0.88rem",
                                                                                    }}
                                                                                >
                                                                                    Button{" "}
                                                                                    {index +
                                                                                        1}
                                                                                </strong>

                                                                                <button
                                                                                    type="button"
                                                                                    className="wa-btn-remove"
                                                                                    onClick={() =>
                                                                                        removeButton(
                                                                                            index
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    Remove
                                                                                </button>

                                                                            </div>

                                                                            <div className="row g-3">

                                                                                <div className="col-md-5">

                                                                                    <div className="wa-field-label">
                                                                                        Type
                                                                                    </div>

                                                                                    <select
                                                                                        className="wa-select"
                                                                                        value={
                                                                                            button.type
                                                                                        }
                                                                                        onChange={(
                                                                                            e
                                                                                        ) =>
                                                                                            updateButton(
                                                                                                index,
                                                                                                "type",
                                                                                                e.target.value
                                                                                            )
                                                                                        }
                                                                                    >

                                                                                        {BUTTON_TYPES.map(
                                                                                            (
                                                                                                type
                                                                                            ) => (
                                                                                                <option
                                                                                                    key={
                                                                                                        type.value
                                                                                                    }
                                                                                                    value={
                                                                                                        type.value
                                                                                                    }
                                                                                                >
                                                                                                    {
                                                                                                        type.label
                                                                                                    }
                                                                                                </option>
                                                                                            )
                                                                                        )}

                                                                                    </select>

                                                                                </div>

                                                                                <div className="col-md-7">

                                                                                    <div className="wa-field-label">
                                                                                        Button text
                                                                                    </div>

                                                                                    <input
                                                                                        type="text"
                                                                                        maxLength={
                                                                                            25
                                                                                        }
                                                                                        className="wa-input"
                                                                                        value={
                                                                                            button.text
                                                                                        }
                                                                                        onChange={(
                                                                                            e
                                                                                        ) =>
                                                                                            updateButton(
                                                                                                index,
                                                                                                "text",
                                                                                                e.target.value
                                                                                            )
                                                                                        }
                                                                                        placeholder="Pay Now"
                                                                                    />

                                                                                </div>

                                                                                {button.type ===
                                                                                    "URL" && (
                                                                                        <>

                                                                                            <div className="col-md-6">

                                                                                                <div className="wa-field-label">
                                                                                                    URL
                                                                                                </div>

                                                                                                <input
                                                                                                    type="text"
                                                                                                    className="wa-input"
                                                                                                    value={
                                                                                                        button.url
                                                                                                    }
                                                                                                    onChange={(
                                                                                                        e
                                                                                                    ) =>
                                                                                                        updateButton(
                                                                                                            index,
                                                                                                            "url",
                                                                                                            e.target.value
                                                                                                        )
                                                                                                    }
                                                                                                    placeholder="https://example.com/pay/{{1}}"
                                                                                                />

                                                                                            </div>

                                                                                            <div className="col-md-6">

                                                                                                <div className="wa-field-label">
                                                                                                    URL example
                                                                                                </div>

                                                                                                <input
                                                                                                    type="text"
                                                                                                    className="wa-input"
                                                                                                    value={
                                                                                                        button.url_example
                                                                                                    }
                                                                                                    onChange={(
                                                                                                        e
                                                                                                    ) =>
                                                                                                        updateButton(
                                                                                                            index,
                                                                                                            "url_example",
                                                                                                            e.target.value
                                                                                                        )
                                                                                                    }
                                                                                                    placeholder="https://example.com/pay/123"
                                                                                                />

                                                                                            </div>

                                                                                        </>
                                                                                    )}

                                                                                {button.type ===
                                                                                    "PHONE_NUMBER" && (
                                                                                        <div className="col-12">

                                                                                            <div className="wa-field-label">
                                                                                                Phone number
                                                                                            </div>

                                                                                            <input
                                                                                                type="text"
                                                                                                className="wa-input"
                                                                                                value={
                                                                                                    button.phone_number
                                                                                                }
                                                                                                onChange={(
                                                                                                    e
                                                                                                ) =>
                                                                                                    updateButton(
                                                                                                        index,
                                                                                                        "phone_number",
                                                                                                        e.target.value
                                                                                                    )
                                                                                                }
                                                                                                placeholder="+919876543210"
                                                                                            />

                                                                                        </div>
                                                                                    )}

                                                                            </div>

                                                                        </div>
                                                                    )
                                                                )}

                                                            </div>

                                                        </div>

                                                    </>
                                                ) : (

                                                    /* =================================================
                                                       AUTHENTICATION
                                                    ================================================= */

                                                    <div className="wa-section">

                                                        <div className="wa-section-head">

                                                            <h6 className="mb-0">
                                                                Authentication template
                                                            </h6>

                                                        </div>

                                                        <div className="wa-section-body">

                                                            <label
                                                                className="d-flex align-items-center gap-2 mb-4"
                                                                style={{
                                                                    fontSize:
                                                                        "0.88rem",
                                                                    cursor:
                                                                        "pointer",
                                                                }}
                                                            >

                                                                <input
                                                                    type="checkbox"
                                                                    checked={
                                                                        form.auth_add_security_recommendation
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        updateForm(
                                                                            "auth_add_security_recommendation",
                                                                            e.target.checked
                                                                        )
                                                                    }
                                                                    style={{
                                                                        accentColor:
                                                                            "var(--wa-accent)",
                                                                        width: 16,
                                                                        height: 16,
                                                                    }}
                                                                />

                                                                Add security recommendation

                                                            </label>

                                                            <div className="row">

                                                                <div className="col-md-6 mb-3">

                                                                    <div className="wa-field-label">
                                                                        Code expiration (minutes)
                                                                    </div>

                                                                    <input
                                                                        type="number"
                                                                        min="1"
                                                                        className="wa-input"
                                                                        value={
                                                                            form.auth_code_expiration_minutes
                                                                        }
                                                                        onChange={(
                                                                            e
                                                                        ) =>
                                                                            updateForm(
                                                                                "auth_code_expiration_minutes",
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                    />

                                                                </div>

                                                                <div className="col-md-6 mb-3">

                                                                    <div className="wa-field-label">
                                                                        OTP button type
                                                                    </div>

                                                                    <select
                                                                        className="wa-select"
                                                                        value={
                                                                            form.auth_otp_type
                                                                        }
                                                                        onChange={(
                                                                            e
                                                                        ) =>
                                                                            updateForm(
                                                                                "auth_otp_type",
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                    >

                                                                        <option value="COPY_CODE">
                                                                            COPY_CODE
                                                                        </option>

                                                                        <option value="ONE_TAP">
                                                                            ONE_TAP
                                                                        </option>

                                                                    </select>

                                                                </div>

                                                                <div className="col-md-6 mb-3">

                                                                    <div className="wa-field-label">
                                                                        Button text
                                                                    </div>

                                                                    <input
                                                                        type="text"
                                                                        className="wa-input"
                                                                        value={
                                                                            form.auth_button_text
                                                                        }
                                                                        onChange={(
                                                                            e
                                                                        ) =>
                                                                            updateForm(
                                                                                "auth_button_text",
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                    />

                                                                </div>

                                                                <div className="col-md-6 mb-3">

                                                                    <div className="wa-field-label">
                                                                        Autofill text
                                                                    </div>

                                                                    <input
                                                                        type="text"
                                                                        className="wa-input"
                                                                        value={
                                                                            form.auth_autofill_text
                                                                        }
                                                                        onChange={(
                                                                            e
                                                                        ) =>
                                                                            updateForm(
                                                                                "auth_autofill_text",
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                        placeholder="Autofill code"
                                                                    />

                                                                </div>

                                                                {form.auth_otp_type ===
                                                                    "ONE_TAP" && (
                                                                        <>

                                                                            <div className="col-md-6 mb-3">

                                                                                <div className="wa-field-label">
                                                                                    Android package name
                                                                                </div>

                                                                                <input
                                                                                    type="text"
                                                                                    className="wa-input"
                                                                                    value={
                                                                                        form.auth_package_name
                                                                                    }
                                                                                    onChange={(
                                                                                        e
                                                                                    ) =>
                                                                                        updateForm(
                                                                                            "auth_package_name",
                                                                                            e.target.value
                                                                                        )
                                                                                    }
                                                                                    placeholder="com.example.app"
                                                                                />

                                                                            </div>

                                                                            <div className="col-md-6 mb-3">

                                                                                <div className="wa-field-label">
                                                                                    App signature hash
                                                                                </div>

                                                                                <input
                                                                                    type="text"
                                                                                    className="wa-input"
                                                                                    value={
                                                                                        form.auth_signature_hash
                                                                                    }
                                                                                    onChange={(
                                                                                        e
                                                                                    ) =>
                                                                                        updateForm(
                                                                                            "auth_signature_hash",
                                                                                            e.target.value
                                                                                        )
                                                                                    }
                                                                                />

                                                                            </div>

                                                                        </>
                                                                    )}

                                                            </div>

                                                        </div>

                                                    </div>
                                                )}

                                                {/* CREATE BUTTON */}

                                                <div className="d-flex justify-content-end mt-4">

                                                    <button
                                                        type="submit"
                                                        className="wa-btn-primary"
                                                        disabled={
                                                            loading ||
                                                            headerUploading
                                                        }
                                                    >
                                                        {loading
                                                            ? "Creating..."
                                                            : headerUploading
                                                                ? "Uploading media..."
                                                                : "Create template"}
                                                    </button>

                                                </div>

                                            </div>

                                            {/* =================================================
                                                RIGHT PREVIEW
                                            ================================================= */}

                                            <div className="col-lg-5">

                                                <div
                                                    className="wa-sticky"
                                                    style={{
                                                        position:
                                                            "sticky",
                                                        top: "5rem",
                                                    }}
                                                >

                                                    <div className="wa-eyebrow">
                                                        Live preview
                                                    </div>

                                                    <div className="wa-phone">

                                                        <div className="wa-phone-inner">

                                                            <div className="wa-phone-head">

                                                                <div className="wa-phone-avatar">
                                                                    {(
                                                                        form.name ||
                                                                        "T"
                                                                    )
                                                                        .charAt(
                                                                            0
                                                                        )
                                                                        .toUpperCase()}
                                                                </div>

                                                                <div
                                                                    style={{
                                                                        lineHeight:
                                                                            1.25,
                                                                    }}
                                                                >

                                                                    <div
                                                                        style={{
                                                                            fontWeight:
                                                                                600,
                                                                            fontSize:
                                                                                "0.92rem",
                                                                        }}
                                                                    >
                                                                        {form.name ||
                                                                            "New template"}
                                                                    </div>

                                                                    <div
                                                                        style={{
                                                                            fontSize:
                                                                                "0.72rem",
                                                                            opacity:
                                                                                0.8,
                                                                        }}
                                                                    >
                                                                        {
                                                                            form.category
                                                                        }
                                                                        {" · "}
                                                                        {
                                                                            LANGUAGES.find(
                                                                                (
                                                                                    l
                                                                                ) =>
                                                                                    l.value ===
                                                                                    form.language
                                                                            )
                                                                                ?.label
                                                                        }
                                                                    </div>

                                                                </div>

                                                            </div>

                                                            <div className="wa-phone-body">

                                                                <div className="wa-bubble">

                                                                    {/* TEXT HEADER */}

                                                                    {form.header_type ===
                                                                        "TEXT" &&
                                                                        form.header_text && (
                                                                            <div className="fw-semibold mb-1">

                                                                                {renderWhatsAppText(
                                                                                    form.header_text,
                                                                                    headerSamplesMap
                                                                                )}

                                                                            </div>
                                                                        )}

                                                                    {/* IMAGE */}

                                                                    {form.header_type ===
                                                                        "IMAGE" && (
                                                                            <>
                                                                                {headerPreviewUrl ? (
                                                                                    <div
                                                                                        style={{
                                                                                            marginBottom:
                                                                                                "8px",
                                                                                            borderRadius:
                                                                                                "8px",
                                                                                            overflow:
                                                                                                "hidden",
                                                                                        }}
                                                                                    >

                                                                                        <img
                                                                                            src={
                                                                                                headerPreviewUrl
                                                                                            }
                                                                                            alt="Header preview"
                                                                                            style={{
                                                                                                width:
                                                                                                    "100%",
                                                                                                maxHeight:
                                                                                                    "300px",
                                                                                                objectFit:
                                                                                                    "cover",
                                                                                                display:
                                                                                                    "block",
                                                                                            }}
                                                                                        />

                                                                                    </div>
                                                                                ) : (
                                                                                    <div className="wa-bubble-media">

                                                                                        <Icon
                                                                                            path={
                                                                                                ICONS.image
                                                                                            }
                                                                                            size={
                                                                                                28
                                                                                            }
                                                                                        />

                                                                                        Image

                                                                                    </div>
                                                                                )}
                                                                            </>
                                                                        )}

                                                                    {/* VIDEO */}

                                                                    {form.header_type ===
                                                                        "VIDEO" && (
                                                                            <>
                                                                                {headerPreviewUrl ? (
                                                                                    <div
                                                                                        style={{
                                                                                            marginBottom:
                                                                                                "8px",
                                                                                            borderRadius:
                                                                                                "8px",
                                                                                            overflow:
                                                                                                "hidden",
                                                                                            background:
                                                                                                "#000",
                                                                                        }}
                                                                                    >

                                                                                        <video
                                                                                            src={
                                                                                                headerPreviewUrl
                                                                                            }
                                                                                            controls
                                                                                            muted
                                                                                            style={{
                                                                                                width:
                                                                                                    "100%",
                                                                                                maxHeight:
                                                                                                    "300px",
                                                                                                display:
                                                                                                    "block",
                                                                                            }}
                                                                                        />

                                                                                    </div>
                                                                                ) : (
                                                                                    <div className="wa-bubble-media">

                                                                                        <Icon
                                                                                            path={
                                                                                                ICONS.video
                                                                                            }
                                                                                            size={
                                                                                                28
                                                                                            }
                                                                                        />

                                                                                        Video

                                                                                    </div>
                                                                                )}
                                                                            </>
                                                                        )}

                                                                    {/* DOCUMENT */}

                                                                    {form.header_type ===
                                                                        "DOCUMENT" && (
                                                                            <div
                                                                                className="wa-bubble-media"
                                                                                style={{
                                                                                    minHeight:
                                                                                        "120px",
                                                                                }}
                                                                            >

                                                                                <Icon
                                                                                    path={
                                                                                        ICONS.file
                                                                                    }
                                                                                    size={
                                                                                        38
                                                                                    }
                                                                                />

                                                                                <strong>
                                                                                    {headerFile?.name ||
                                                                                        "Document"}
                                                                                </strong>

                                                                            </div>
                                                                        )}

                                                                    {/* LOCATION */}

                                                                    {form.header_type ===
                                                                        "LOCATION" && (
                                                                            <div
                                                                                className="wa-bubble-media"
                                                                                style={{
                                                                                    minHeight:
                                                                                        "120px",
                                                                                }}
                                                                            >

                                                                                <Icon
                                                                                    path={
                                                                                        ICONS.pin
                                                                                    }
                                                                                    size={
                                                                                        32
                                                                                    }
                                                                                />

                                                                                <strong>
                                                                                    Location
                                                                                </strong>

                                                                                <span>
                                                                                    Location header
                                                                                </span>

                                                                            </div>
                                                                        )}

                                                                    {/* BODY */}

                                                                    {form.category !==
                                                                        "AUTHENTICATION" ? (
                                                                        form.body_text ? (
                                                                            <div
                                                                                style={{
                                                                                    whiteSpace:
                                                                                        "pre-wrap",
                                                                                    wordBreak:
                                                                                        "break-word",
                                                                                }}
                                                                            >
                                                                                {renderWhatsAppText(
                                                                                    form.body_text,
                                                                                    bodySamples
                                                                                )}
                                                                            </div>
                                                                        ) : (
                                                                            <div
                                                                                className="text-muted"
                                                                                style={{
                                                                                    fontSize:
                                                                                        "0.85rem",
                                                                                }}
                                                                            >
                                                                                Body text will appear here.
                                                                            </div>
                                                                        )
                                                                    ) : (
                                                                        <div>

                                                                            <div className="fw-semibold">
                                                                                Your verification code
                                                                            </div>

                                                                            <div className="wa-otp-pill">
                                                                                123456
                                                                            </div>

                                                                            {form.auth_add_security_recommendation && (
                                                                                <div
                                                                                    className="text-muted mt-2"
                                                                                    style={{
                                                                                        fontSize:
                                                                                            "0.75rem",
                                                                                    }}
                                                                                >
                                                                                    For your security, don't share this code.
                                                                                </div>
                                                                            )}

                                                                        </div>
                                                                    )}

                                                                    {/* FOOTER */}

                                                                    {form.category !==
                                                                        "AUTHENTICATION" &&
                                                                        form.footer_text && (
                                                                            <div
                                                                                className="text-muted mt-2"
                                                                                style={{
                                                                                    fontSize:
                                                                                        "0.75rem",
                                                                                }}
                                                                            >
                                                                                {
                                                                                    form.footer_text
                                                                                }
                                                                            </div>
                                                                        )}

                                                                    <div className="wa-bubble-time">
                                                                        12:00 PM
                                                                    </div>

                                                                    {/* BUTTONS */}

                                                                    {form.category !==
                                                                        "AUTHENTICATION" &&
                                                                        form.buttons.length >
                                                                        0 && (
                                                                            <div className="border-top mt-2 pt-1">

                                                                                {form.buttons.map(
                                                                                    (
                                                                                        button,
                                                                                        index
                                                                                    ) => (
                                                                                        <div
                                                                                            className="wa-bubble-btn"
                                                                                            key={
                                                                                                index
                                                                                            }
                                                                                        >
                                                                                            {button.text ||
                                                                                                "Button"}
                                                                                        </div>
                                                                                    )
                                                                                )}

                                                                            </div>
                                                                        )}

                                                                    {/* AUTH BUTTON */}

                                                                    {form.category ===
                                                                        "AUTHENTICATION" && (
                                                                            <div className="border-top mt-2 pt-1">

                                                                                <div className="wa-bubble-btn">
                                                                                    {form.auth_button_text ||
                                                                                        "Copy Code"}
                                                                                </div>

                                                                            </div>
                                                                        )}

                                                                </div>

                                                            </div>

                                                        </div>

                                                    </div>

                                                </div>

                                            </div>

                                        </div>

                                    </form>

                                </div>

                            </div>

                        </div>

                    </div>

                </div>
            </div>
        </>
    );
}